import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import { fetchPreviewWorksCached } from 'js/bulk_download/bulk_download_counts_cache';
import {
  BundleWork,
  displayTitle,
} from 'js/bulk_download/work_cards_shared/types';
import { Work as SidebarWork } from 'js/liswp/Sidebar/hooks/types';

import { Shimmer } from 'js/liswp_blocks/shared/Shimmer';
import { PdfMiniChip } from 'js/liswp_blocks/shared/PdfMiniChip';
import { authorLine } from 'js/liswp_blocks/shared/authorLine';
import { formatStat } from 'js/liswp_blocks/shared/formatStat';
import { syntheticRelevancy } from 'js/liswp_blocks/shared/relevancy';
import { tPlural, t } from 'js/intl/t';
import 'js/i18n/page_translations/liswp_blocks/en.wjs';

/**
 * Shared PDF-pack contents preview for the block-page heroes (tabs,
 * pick_download): the pack's related papers as non-interactive rows —
 * thumbnail, title, "by A +2 more | 1.2K views" meta (phones drop the author
 * segment per the comps), and a relevancy badge (above the title on phones,
 * at the row's right edge on desktop) — fading out at the bottom into a "+N
 * related research papers" count.
 *
 * Shared rather than forked per variant (liswp_blocks/AGENTS.md Rule 2); the
 * comps differ only in density, hence `variant`:
 *  - 'tabs' (2190:22910): self-padded 16×20 rows, 50×72 thumbs, semibold
 *    titles, 28px/14px badges, beige page outlines.
 *  - 'pickDownload' (2190:23032): 24px-gapped flush rows, 40×58 thumbs,
 *    regular titles, 22px/12px badges, light-grey outlines.
 *
 * The rows read the same bulk-download bundle as the modal (via
 * fetchPreviewWorksCached); bundle relevancy is used when available, while
 * legacy sidebar-shaped rows retain the synthetic fallback (see ./relevancy).
 * Rows are a non-interactive preview — no links, so no impression/nav
 * instrumentation.
 * The consumer owns the clipping container (fixed height + overflow hidden)
 * and the surrounding card chrome.
 */

export type PackPreviewVariant = 'tabs' | 'pickDownload';

export type PackPreviewWork = SidebarWork | BundleWork;

export type PackPreviewState = 'loading' | 'empty' | 'available';

/** The bulk-download bundle's related works for the rows. */
export function usePackPreviewWorks(
  workId: number,
  maxRows = 6,
  {
    packState = 'available',
  }: {
    packState?: PackPreviewState;
  } = {},
): {
  relatedWorks: BundleWork[];
  rowViews: Record<string, number>;
  loading: boolean;
} {
  const [relatedWorks, setRelatedWorks] = useState<BundleWork[]>([]);
  const [rowViews, setRowViews] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (packState !== 'available') {
      setRelatedWorks([]);
      setRowViews({});
      setLoading(packState === 'loading');
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    fetchPreviewWorksCached(workId)
      .then((works) => {
        if (cancelled) return;
        const related = works
          .filter((work) => work.bucket !== 'thePaperItself')
          .slice(0, maxRows);
        setRelatedWorks(related);
        setRowViews(
          Object.fromEntries(
            related.map((work) => [String(work.id), work.viewCount]),
          ),
        );
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setRelatedWorks([]);
        setRowViews({});
      });

    return () => {
      cancelled = true;
    };
  }, [maxRows, packState, workId]);

  return { relatedWorks, rowViews, loading };
}

/** Bundle rows' publication years keyed by work id, for the rows' optional
 *  "| year |" meta segment (rows without a year just drop the segment). */
export function bundleRowYears(
  relatedWorks: BundleWork[],
): Record<string, number> {
  return Object.fromEntries(
    relatedWorks.flatMap((relatedWork) =>
      relatedWork.year != null
        ? [[String(relatedWork.id), relatedWork.year]]
        : [],
    ),
  );
}

const Rows = styled.div<{ variant: PackPreviewVariant }>`
  display: flex;
  flex-direction: column;
  width: 100%;

  &.variant-tabs {
    padding-top: var(--spacing-xs-8, 8px);
  }

  &.variant-pickDownload {
    gap: var(--spacing-sm-24, 24px);
    padding-bottom: var(--spacing-xs-16, 16px);
  }
`;

const Row = styled.div<{ variant: PackPreviewVariant }>`
  display: flex;
  gap: var(--spacing-xs-12, 12px);
  align-items: flex-start;
  width: 100%;

  &.variant-tabs {
    padding: var(--spacing-xs-16, 16px) 20px;
  }

  // Explicit no-op so astroturf consumes the prop value (unmatched enum
  // values are forwarded to the DOM as attributes).
  &.variant-pickDownload {
    padding: 0;
  }
`;

const RowThumb = styled.img<{ variant: PackPreviewVariant }>`
  flex-shrink: 0;
  object-fit: cover;
  object-position: top center;
  background: var(--background-white, #ffffff);

  &.variant-tabs {
    width: 50px;
    height: 72px;
    border: 1px solid var(--stroke-beige-light, #ccc9bc);
  }

  &.variant-pickDownload {
    width: 40px;
    height: 58px;
    border: 1px solid var(--stroke-light, #dddde2);
  }
`;

const RowThumbWrap = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
`;

const RowBody = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
  min-width: 0;
`;

// Row title, clamped to two lines. tabs uses body/small-bold, pick_download
// body/small regular, per the comps.
const RowTitle = styled.p<{ variant: PackPreviewVariant; weight?: 'bold' }>`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-sm-size, 14px);
  line-height: var(--type-body-sm-line-height, 20px);
  color: var(--text-primary, #141413);

  &.variant-tabs {
    font-weight: var(--type-body-sm-bold-font-weight, 600);
  }

  &.variant-pickDownload {
    font-weight: var(--type-body-sm-font-weight, 400);
  }

  &.weight-bold {
    font-weight: var(--type-body-sm-bold-font-weight, 600);
  }
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-8, 8px);
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-xs-size, 12px);
  line-height: var(--type-body-xs-line-height, 16px);
  color: var(--text-secondary, #535366);
`;

// The comps' phone rows drop the author segment (views only); desktop keeps
// "by A +2 more |".
const RowMetaAuthor = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  display: inline-flex;
  gap: var(--spacing-xs-8, 8px);

  @include breakpoint(small, medium) {
    display: none;
  }
`;

// The "98% relevancy" badge (lime fill + success outline). Desktop keeps it at
// the row's right edge; phones move it above the title.
const RelevancyBadge = styled.span<{
  variant: PackPreviewVariant;
  placement: 'desktop' | 'mobile';
}>`
  @import 'stylesheets/design_system/breakpoint';

  align-items: center;
  background: var(--brand-colors-expanded-colors-lime-100, #f2f5ef);
  border: 1px solid var(--brand-colors-success-900, #035c0f);
  border-radius: var(--border-radius-radius-md, 8px);
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  color: var(--brand-colors-expanded-colors-lime-900, #333e2c);
  white-space: nowrap;

  &.variant-tabs {
    height: 28px;
    padding: 0 var(--spacing-xs-8, 8px);
    font-size: var(--type-body-sm-size, 14px);
    line-height: var(--type-body-sm-line-height, 20px);
  }

  &.variant-pickDownload {
    height: 22px;
    padding: 0 6px;
    font-size: var(--type-body-xs-size, 12px);
    line-height: var(--type-body-xs-line-height, 16px);
    font-weight: var(--type-body-xs-bold-font-weight, 600);
  }

  &.placement-desktop {
    display: inline-flex;
    flex-shrink: 0;

    @include breakpoint(small, medium) {
      display: none;
    }
  }

  &.placement-mobile {
    display: none;

    @include breakpoint(small, medium) {
      display: inline-flex;
      align-self: flex-start;
    }
  }
`;

const RowSkeleton = styled(Shimmer)<{ variant: PackPreviewVariant }>`
  width: 100%;

  &.variant-tabs {
    height: 72px;
    margin: var(--spacing-xs-16, 16px) 0;
  }

  &.variant-pickDownload {
    height: 58px;
  }
`;

// Bottom fade into the panel background with the "+N related research papers"
// count, per the comps. Absolutely positioned within the consumer's clipping
// container.
const FadeOut = styled.div<{ size?: 'v2'; tone?: 'card' }>`
  @import 'stylesheets/design_system/breakpoint';

  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: var(--spacing-xs-4, 4px);
  height: 109px;
  padding-bottom: var(--spacing-xs-16, 16px);
  background: linear-gradient(180deg, rgba(238, 242, 249, 0) 13%, #eef2f9 56%);

  &.size-v2 {
    @include breakpoint(small, medium) {
      height: 119px;
    }
  }

  // The V3 white pack cards (PackContentsCard) fade into the card's white,
  // not the panel's light blue.
  &.tone-card {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 13%,
      #ffffff 56%
    );
  }
`;

const FadeCount = styled.span`
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-lg-size, 20px);
  line-height: var(--type-body-lg-line-height, 32px);
  font-weight: var(--type-body-lg-bold-font-weight, 600);
  color: var(--text-primary, #141413);
  /* Keeps "+N" in order in RTL; CSS rather than dir= so shipped markup is unchanged. */
  direction: ltr;
  unicode-bidi: isolate;
`;

const FadeLabel = styled.span`
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-xs-size, 12px);
  line-height: var(--type-body-xs-line-height, 16px);
  color: var(--text-primary, #141413);
  // Baseline-align the small label with the big count per the comps.
  padding-bottom: 6px;
`;

export type PackPreviewRowsProps = {
  variant: PackPreviewVariant;
  relatedWorks: PackPreviewWork[];
  rowViews: Record<string, number>;
  rowYears?: Record<string, number>;
  // Explicit loading distinguishes a loaded-empty bundle from legacy empty rows.
  loading?: boolean;
  thumbChips?: boolean;
  meta?: 'default' | 'yearviews';
  boldTitles?: boolean;
  // Off suppresses the "N% relevancy" line. Needed wherever the rows are built from related
  // works rather than a scored pack: relevancy then falls through to syntheticRelevancy(i),
  // which is a positional placeholder, not a measurement.
  showRelevancy?: boolean;
};

export const PackPreviewRows: React.FC<PackPreviewRowsProps> = ({
  variant,
  relatedWorks,
  rowViews,
  rowYears,
  loading = relatedWorks.length === 0,
  thumbChips,
  meta = 'default',
  boldTitles,
  showRelevancy = true,
}) => (
  <Rows variant={variant}>
    {loading && [0, 1, 2].map((i) => <RowSkeleton key={i} variant={variant} />)}
    {!loading &&
      relatedWorks.map((relatedWork, i) => {
        const percent = showRelevancy
          ? 'thumbnailUrl' in relatedWork
            ? relatedWork.relevancy
            : syntheticRelevancy(i)
          : undefined;
        const relevancy =
          percent != null
            ? t('liswp_blocks.pack_preview.relevancy_percent', { percent })
            : undefined;
        const rowAuthors =
          typeof relatedWork.authors === 'string'
            ? relatedWork.authors
            : authorLine(relatedWork.authors);
        const rowViewCount = rowViews[String(relatedWork.id)];
        const rowYear = rowYears?.[String(relatedWork.id)];
        const thumb = (
          <RowThumb
            variant={variant}
            src={
              'thumbnailUrl' in relatedWork
                ? relatedWork.thumbnailUrl
                : relatedWork.image
            }
            alt=""
          />
        );
        return (
          <Row key={relatedWork.id} variant={variant}>
            {thumbChips ? (
              <RowThumbWrap>
                {thumb}
                <PdfMiniChip />
              </RowThumbWrap>
            ) : (
              thumb
            )}
            <RowBody>
              {relevancy && (
                <RelevancyBadge variant={variant} placement="mobile">
                  {relevancy}
                </RelevancyBadge>
              )}
              <RowTitle
                variant={variant}
                weight={boldTitles ? 'bold' : undefined}
              >
                {displayTitle(relatedWork)}
              </RowTitle>
              {/* Segments drop out when their data is missing rather than
                    leaving dangling separators. The author segment's separator
                    lives inside RowMetaAuthor so it hides with it on phones. */}
              <RowMeta>
                {rowAuthors && meta !== 'yearviews' && (
                  <RowMetaAuthor>
                    <span>
                      {t('liswp_blocks.pack_preview.by_authors', {
                        authors: rowAuthors,
                      })}
                    </span>
                    {(rowYear !== undefined || rowViewCount !== undefined) && (
                      <span>|</span>
                    )}
                  </RowMetaAuthor>
                )}
                {rowYear !== undefined && (
                  <>
                    <span>{rowYear}</span>
                    {rowViewCount !== undefined && <span>|</span>}
                  </>
                )}
                {rowViewCount !== undefined && (
                  <span>
                    {tPlural(
                      'liswp_blocks.pack_preview.views_count',
                      rowViewCount,
                      { views: formatStat(rowViewCount) },
                    )}
                  </span>
                )}
              </RowMeta>
            </RowBody>
            {relevancy && (
              <RelevancyBadge variant={variant} placement="desktop">
                {relevancy}
              </RelevancyBadge>
            )}
          </Row>
        );
      })}
  </Rows>
);

/** The "+N related research papers" fade; render inside the clipping box.
 *  The V3 checkbox cards restate exclusion in the label ("… not included")
 *  and fade to the card's white instead of the panel's blue. */
export const PackPreviewFade: React.FC<{
  relatedCount: number;
  size?: 'v2';
  tone?: 'card';
  label?: string;
}> = ({ relatedCount, size, tone, label }) => (
  <FadeOut size={size} tone={tone}>
    <FadeCount>+{formatStat(relatedCount)}</FadeCount>
    <FadeLabel>
      {label ??
        tPlural('liswp_blocks.pack_preview.fade_label_default', relatedCount)}
    </FadeLabel>
  </FadeOut>
);

export default PackPreviewRows;
